'use client';

/**
 * React hook for iframe authentication
 *
 * This hook handles the complete iframe auth flow:
 * 1. Detects if running in an iframe
 * 2. Listens for postMessage from parent with tokens
 * 3. Establishes Supabase session from received tokens
 * 4. Notifies parent of auth state changes
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import logger from '@/lib/logger';
import { createClient } from '@/lib/supabase/client';
import {
  isInIframe,
  isAllowedParentOrigin,
  getIframeContext,
  type IframeContext,
} from '@/lib/iframe/context';
import {
  parseMessage,
  sendToParent,
  IframeMessages,
  isValidAuthTokenPayload,
  type AuthTokenPayload,
  type AuthStatePayload,
} from '@/lib/iframe/messages';

/**
 * Options for useIframeAuth hook
 */
interface UseIframeAuthOptions {
  /** Called when session is successfully established */
  onAuthEstablished?: (userId: string) => void;
  /** Called when auth fails */
  onAuthFailed?: (error: string) => void;
  /** Whether to automatically request tokens on mount */
  autoRequest?: boolean;
}

/**
 * Return type for useIframeAuth hook
 */
interface UseIframeAuthReturn {
  /** Current iframe context */
  iframeContext: IframeContext;
  /** Whether initial auth is in progress */
  isInitializing: boolean;
  /** Error message if auth failed */
  error: string | null;
  /** Whether session has been established */
  isAuthenticated: boolean;
  /** Request auth tokens from parent */
  requestAuthFromParent: () => void;
  /** Notify parent to handle logout */
  notifyParentLogout: () => void;
  /** Notify parent to initiate login */
  notifyParentLoginRequest: () => void;
}

/**
 * Hook for handling authentication in iframe context
 */
export function useIframeAuth(options: UseIframeAuthOptions = {}): UseIframeAuthReturn {
  const {
    onAuthEstablished,
    onAuthFailed,
    autoRequest = true,
  } = options;

  // Get iframe context once on mount (doesn't change during session)
  const [iframeContext] = useState<IframeContext>(() => getIframeContext());

  // Auth state
  const [isInitializing, setIsInitializing] = useState(iframeContext.isIframe);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Prevent double initialization in React StrictMode
  const hasInitialized = useRef(false);
  const mountedRef = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const supabase = createClient();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  /**
   * Establish session from received tokens
   */
  const establishSession = useCallback(async (tokens: AuthTokenPayload) => {
    try {
      const { data, error: sessionError } = await supabase.auth.setSession({
        access_token: tokens.accessToken,
        refresh_token: tokens.refreshToken,
      });

      if (sessionError) {
        throw sessionError;
      }

      if (data.user) {
        if (!mountedRef.current) return;
        setIsAuthenticated(true);
        setIsInitializing(false);
        setError(null);
        onAuthEstablished?.(data.user.id);

        // Notify parent of successful auth
        const statePayload: AuthStatePayload = {
          isAuthenticated: true,
          userId: data.user.id,
          email: data.user.email ?? undefined,
          role: data.user.app_metadata?.role as string | undefined,
        };
        sendToParent(IframeMessages.stateChange(statePayload));
      } else {
        throw new Error('No user data in session response');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to establish session';
      if (!mountedRef.current) return;
      setError(errorMsg);
      setIsInitializing(false);
      setIsAuthenticated(false);
      onAuthFailed?.(errorMsg);

      // Notify parent of auth failure
      sendToParent(IframeMessages.stateChange({
        isAuthenticated: false,
      }));
    }
  }, [supabase, onAuthEstablished, onAuthFailed]);

  /**
   * Handle incoming messages from parent
   */
  const handleMessage = useCallback(async (event: MessageEvent) => {
    // Validate origin
    if (!isAllowedParentOrigin(event.origin)) {
      logger.warn('useIframeAuth', 'Message from unauthorized origin', { data: event.origin });
      return;
    }

    const message = parseMessage(event);
    if (!message) return;

    switch (message.type) {
      case 'AUTH_TOKEN_RESPONSE':
        if (isValidAuthTokenPayload(message.payload)) {
          await establishSession(message.payload);
        } else if (mountedRef.current) {
          const errorMsg = 'Invalid auth token payload received';
          setError(errorMsg);
          setIsInitializing(false);
          onAuthFailed?.(errorMsg);
        }
        break;

      // Handle other message types as needed
      default:
        // Unknown message type - ignore
        break;
    }
  }, [establishSession, onAuthFailed]);

  /**
   * Request auth token from parent
   */
  const requestAuthFromParent = useCallback(() => {
    if (!iframeContext.isIframe) return;
    sendToParent(IframeMessages.requestToken());
  }, [iframeContext.isIframe]);

  /**
   * Notify parent to handle logout
   */
  const notifyParentLogout = useCallback(() => {
    if (!iframeContext.isIframe) return;
    sendToParent(IframeMessages.requestLogout());
  }, [iframeContext.isIframe]);

  /**
   * Notify parent to initiate login
   */
  const notifyParentLoginRequest = useCallback(() => {
    if (!iframeContext.isIframe) return;
    sendToParent(IframeMessages.requestLogin());
  }, [iframeContext.isIframe]);

  /**
   * Initialize auth by checking existing session or requesting tokens
   */
  useEffect(() => {
    if (!iframeContext.isIframe || hasInitialized.current) return;
    hasInitialized.current = true;

    const initializeAuth = async () => {
      // Check if we already have a valid session
      const { data: { user } } = await supabase.auth.getUser();
      if (!mountedRef.current) return;
      if (user) {
        setIsAuthenticated(true);
        setIsInitializing(false);

        // Notify parent of existing auth
        sendToParent(IframeMessages.stateChange({
          isAuthenticated: true,
          userId: user.id,
          email: user.email ?? undefined,
          role: user.app_metadata?.role as string | undefined,
        }));
        return;
      }

      // No URL tokens and no existing session - request from parent
      if (autoRequest) {
        // Signal that iframe is ready
        sendToParent(IframeMessages.ready());

        // Request tokens from parent
        requestAuthFromParent();

        // Set timeout for parent response — use mountedRef to avoid stale closure
        timeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            // Parent didn't respond in time
            setIsInitializing(false);
            // Don't set error - just proceed without auth
          }
        }, 5000);
      } else {
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, [iframeContext.isIframe, autoRequest, establishSession, requestAuthFromParent, supabase]);

  /**
   * Set up message listener
   */
  useEffect(() => {
    if (!iframeContext.isIframe) return;

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [iframeContext.isIframe, handleMessage]);

  return {
    iframeContext,
    isInitializing,
    error,
    isAuthenticated,
    requestAuthFromParent,
    notifyParentLogout,
    notifyParentLoginRequest,
  };
}
